import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  ArrowRight,
  CalendarCheck01,
  Check,
  ChevronLeft,
  ChevronRight,
  MarkerPin01,
  Tool02,
} from "@untitledui/icons"
import { toast } from "sonner"
import { cx } from "@/lib/utils/cx"
import { formatTime } from "@/lib/utils/format-time"
import {
  publicConsumerApi,
  type PublicSchedulingRules,
  type PublicShopProfile,
} from "@/lib/api/public-consumer"
import { DetailRow, FlowCta, FlowHeading, PhasePane, StickyActions } from "./flow-chrome"
import { buildAvailableDates, buildTimeSlots, toISODate, type AppointmentType } from "./booking-slots"

// Consumer booking, inside the flow frame: type → day and time → confirm. The
// frame's Back steps through these; on success the orchestrator moves to done.
// No price anywhere — cost lives on the tracking page, from the shop.

export interface BookedInfo {
  type: AppointmentType
  date: Date
  time: string
}

type Step = "type" | "date" | "confirm"

export function BookingFlow({
  shop,
  trackToken,
  onBooked,
  onCancel,
  setBack,
}: {
  shop: PublicShopProfile
  trackToken: string
  onBooked: (info: BookedInfo) => void
  onCancel: () => void
  // Hands this step's Back to the flow frame (type → offer, date → type…).
  setBack: (handler: (() => void) | null) => void
}) {
  const [step, setStep] = useState<Step>("type")
  const [type, setType] = useState<AppointmentType | null>(null)
  const [rules, setRules] = useState<PublicSchedulingRules | null>(null)
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const cancelRef = useRef(onCancel)
  useLayoutEffect(() => {
    cancelRef.current = onCancel
  })

  useEffect(() => {
    let alive = true
    publicConsumerApi
      .getSchedulingRules(shop.id)
      .then((r) => alive && setRules(r))
      .catch(() => alive && setRules(null))
    return () => {
      alive = false
    }
  }, [shop.id])

  useEffect(() => {
    const back =
      step === "type"
        ? () => cancelRef.current()
        : step === "date"
          ? () => {
              setStep("type")
              setSelectedDate(null)
              setSelectedTime(null)
            }
          : () => setStep("date")
    setBack(back)
    return () => setBack(null)
  }, [step, setBack])

  const availableDates = useMemo(
    () => (rules && type ? buildAvailableDates(rules, type, currentMonth, new Date()) : []),
    [rules, type, currentMonth],
  )
  const timeSlots = useMemo(
    () => (rules && type && selectedDate ? buildTimeSlots(rules, type, selectedDate, new Date()) : []),
    [rules, type, selectedDate],
  )

  const handleConfirm = async () => {
    if (!type || !selectedDate || !selectedTime) return
    setConfirming(true)
    try {
      await publicConsumerApi.bookAppointment(trackToken, {
        type,
        date: toISODate(selectedDate),
        time: selectedTime,
      })
      onBooked({ type, date: selectedDate, time: selectedTime })
    } catch {
      toast.error("Couldn’t confirm that time. Please try another.")
    } finally {
      setConfirming(false)
    }
  }

  return (
    <PhasePane className="pt-2">
      <AnimatePresence mode="wait" initial={false}>
        {step === "type" && (
          <Scene key="type">
            <SceneHead
              title="Pick what works for you"
              blurb="Start with a free look, or bring it straight in. Your call."
            />
            <div className="mt-6 space-y-3">
              <TypeCard
                icon={<Tool02 className="size-5" aria-hidden />}
                title="Free inspection"
                body="A quick look at the damage in person. No cost, no commitment."
                onClick={() => {
                  setType("inspection")
                  setStep("date")
                }}
              />
              <TypeCard
                icon={<CalendarCheck01 className="size-5" aria-hidden />}
                title="Drop off for repair"
                body="Ready to go? Drop your car off and the shop gets started."
                onClick={() => {
                  setType("drop_off")
                  setStep("date")
                }}
              />
            </div>
          </Scene>
        )}

        {step === "date" && (
          <Scene key="date">
            <SceneHead
              title="Pick a day and time"
              blurb={type === "inspection" ? "For your free inspection." : "For dropping the car off."}
            />
            <div className="mt-6">
              <Calendar
                month={currentMonth}
                availableDates={availableDates}
                selectedDate={selectedDate}
                onPrevMonth={() =>
                  setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
                }
                onNextMonth={() =>
                  setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
                }
                onPick={(d) => {
                  setSelectedDate(d)
                  setSelectedTime(null)
                }}
              />
            </div>

            {selectedDate && (
              <div className="mt-6">
                <p className="mb-2.5 text-[15px] font-semibold text-ink">
                  {selectedDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" role="radiogroup" aria-label="Time">
                  {timeSlots.map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="radio"
                      aria-checked={selectedTime === t}
                      onClick={() => setSelectedTime(t)}
                      className={cx(
                        "h-12 rounded-xl text-[15px] font-semibold transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink",
                        selectedTime === t
                          ? "bg-ink text-white"
                          : "bg-white text-ink ring-1 ring-ink/12 hover:ring-ink/25",
                      )}
                    >
                      {formatTime(t)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedDate && selectedTime && (
              <StickyActions>
                <FlowCta onClick={() => setStep("confirm")}>
                  Continue
                  <ArrowRight className="size-4" aria-hidden />
                </FlowCta>
              </StickyActions>
            )}
          </Scene>
        )}

        {step === "confirm" && type && selectedDate && selectedTime && (
          <Scene key="confirm">
            <SceneHead title="Confirm your appointment" />
            <dl className="mt-6 divide-y divide-ink/10 border-y border-ink/10">
              <DetailRow
                icon={<CalendarCheck01 className="size-5" aria-hidden />}
                label="When"
                title={selectedDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}
                body={`${formatTime(selectedTime)} · ${type === "inspection" ? "Free inspection" : "Drop-off"}`}
              />
              <DetailRow
                icon={<MarkerPin01 className="size-5" aria-hidden />}
                label="Where"
                title={shop.name}
                body={
                  [shop.street, shop.city, shop.state].filter(Boolean).join(", ") ||
                  "Address shared after booking"
                }
              />
            </dl>

            <section className="mt-6">
              <h3 className="text-[15px] font-semibold text-ink">What to bring</h3>
              <ul className="mt-2 space-y-2">
                {["Driver’s license", "Insurance info (if it's a claim)", "Your keys"].map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-[15px] text-zinc-700">
                    <Check className="size-4 text-signal" aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            <StickyActions>
              <FlowCta onClick={() => void handleConfirm()} loading={confirming}>
                Confirm appointment
                {!confirming && <Check className="size-4" aria-hidden />}
              </FlowCta>
            </StickyActions>
          </Scene>
        )}
      </AnimatePresence>
    </PhasePane>
  )
}

/* ── pieces ── */

function Scene({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.2 }}
      className="flex flex-1 flex-col"
    >
      {children}
    </motion.div>
  )
}

function SceneHead({ title, blurb }: { title: string; blurb?: string }) {
  return (
    <header>
      <FlowHeading className="text-[28px] font-bold leading-[1.15] tracking-tight text-ink">
        {title}
      </FlowHeading>
      {blurb && <p className="mt-2 text-[15px] leading-relaxed text-zinc-600">{blurb}</p>}
    </header>
  )
}

function TypeCard({
  icon,
  title,
  body,
  onClick,
}: {
  icon: React.ReactNode
  title: string
  body: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-start gap-3.5 rounded-2xl bg-white p-4 text-left ring-1 ring-ink/10 transition-colors duration-150 hover:ring-ink/25 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-ink/5 text-ink">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-semibold leading-tight text-ink">{title}</span>
        <span className="mt-1 block text-[14px] leading-snug text-zinc-600">{body}</span>
      </span>
      <ChevronRight
        className="mt-2.5 size-5 shrink-0 text-zinc-400 transition-colors group-hover:text-ink"
        aria-hidden
      />
    </button>
  )
}

function Calendar({
  month,
  availableDates,
  selectedDate,
  onPrevMonth,
  onNextMonth,
  onPick,
}: {
  month: Date
  availableDates: Date[]
  selectedDate: Date | null
  onPrevMonth: () => void
  onNextMonth: () => void
  onPick: (d: Date) => void
}) {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1)
  const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0)
  const startPad = firstDay.getDay()
  const availableKeys = new Set(availableDates.map((d) => d.toDateString()))
  const todayStart = new Date(new Date().setHours(0, 0, 0, 0))

  const cells: React.ReactNode[] = []
  for (let i = 0; i < startPad; i++) cells.push(<div key={`pad-${i}`} className="h-11" />)
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(month.getFullYear(), month.getMonth(), day)
    const available = availableKeys.has(date.toDateString()) && date >= todayStart
    const selected = selectedDate?.toDateString() === date.toDateString()
    cells.push(
      <button
        key={day}
        type="button"
        disabled={!available}
        onClick={() => onPick(date)}
        aria-pressed={selected}
        aria-label={date.toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
        })}
        className={cx(
          "mx-auto grid size-11 place-items-center rounded-full text-[15px] font-semibold transition-colors duration-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink",
          selected
            ? "bg-ink text-white"
            : available
              ? "text-ink hover:bg-ink/5"
              : "cursor-not-allowed text-zinc-300",
        )}
      >
        {day}
      </button>,
    )
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-[16px] font-semibold text-ink">
          {month.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          })}
        </p>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={onPrevMonth}
            className="grid size-11 place-items-center rounded-full text-ink hover:bg-ink/5 focus-visible:outline-2 focus-visible:outline-ink"
            aria-label="Previous month"
          >
            <ChevronLeft className="size-5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onNextMonth}
            className="grid size-11 place-items-center rounded-full text-ink hover:bg-ink/5 focus-visible:outline-2 focus-visible:outline-ink"
            aria-label="Next month"
          >
            <ChevronRight className="size-5" aria-hidden />
          </button>
        </div>
      </div>
      <div className="mb-1 grid grid-cols-7 text-center" aria-hidden>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="py-1 text-[13px] font-semibold text-zinc-500">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-1">{cells}</div>
    </div>
  )
}
